// RITA-1571 (Epic RITA-901) — Auswahlfelder „Zeilenabstand“ und „Abstand nach Absatz“ der Werkzeugleiste
// (freigegebene Skizze vom 29.10.2026): direkt nach der Schriftgröße, beide gelten für das ganze Dokument samt
// Kopf- und Fußzeile, egal wo die Schreibmarke steht. Bezeichnungen wie in Word; das aufgeklappte Feld nennt
// zu jedem Eintrag die Word-Entsprechung. „wie bisher“ erscheint nur, solange eine alte Vorlage ihre alte
// Zeilenhöhe hat (model/abstaende.ts).
//
// Fokus wie bei der Schriftgröße (Schriftgroessenwahl.tsx): Vor dem Öffnen wird die DOM-Auswahl ins Modell
// übernommen. Das Menü läuft ohne MUI-Fokusfalle und ohne MUI-Fokusrückgabe; nach einer Wahl – auch des schon
// gewählten Werts – holt das Feld die Schreibmarke sofort in den Vertrag zurück, ein Abbruch (oder eine Wahl ohne
// Schreibmarke) gibt den Fokus dem Feld. Der Hinweis „Gilt für …“ schließt beim Aufklappen und öffnet nur durch Maus
// oder Fokus auf dem Feld selbst (Abschlussreview: Ereignisse der Liste im Portal steigen im React-Baum bis zum
// Hinweis auf, er stand sonst nach jeder Wahl weiter da).
import React, { useRef, useState } from 'react';
import { Box, ListItemIcon, ListItemText, ListSubheader, MenuItem, TextField, Tooltip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { VertragEditorApi } from '../editor/useVertragEditor';
import {
  ABSATZABSTAENDE,
  BISHERIGE_ZEILENHOEHE,
  absatzabstandBezeichnung,
  absatzabstandEmVon,
  Abstandsstufe,
  WIE_BISHER_HINWEIS,
  ZEILENABSTAENDE,
  zeilenabstandBezeichnung,
} from '../model/abstaende';

export const ZEILENABSTAND_FELDNAME = 'Zeilenabstand';
export const ABSATZABSTAND_FELDNAME = 'Abstand nach Absatz';

/** Wofür die Einstellungen gelten: die Vorlage im Vorlagen-Editor, beim Berater das Dokument des jeweiligen Reiters. */
export type Abstandsgeltung = 'vorlage' | 'dokument';

/** Beim Berater gilt die Einstellung für das gerade bearbeitete Anschreiben bzw. den Vertrag (je Reiter). */
export const GELTUNG_TEXT: Record<Abstandsgeltung, string> = {
  vorlage: 'Gilt für die ganze Vorlage',
  dokument: 'Gilt für das ganze Dokument',
};

interface Props {
  editor: VertragEditorApi;
  gesperrt?: boolean;
  geltung?: Abstandsgeltung;
}

interface FeldProps {
  editor: VertragEditorApi;
  gesperrt: boolean;
  geltung: Abstandsgeltung;
  id: string;
  feldname: string;
  symbol: string;
  stufen: readonly Abstandsstufe[];
  wert: number;
  bezeichnung: string;
  breite: number;
  /** Erklärung zu einem nicht wählbaren Wert, den das Dokument noch hat. */
  fremderHinweis: string;
  waehlen: (wert: number) => void;
}

function Abstandsfeld({
  editor,
  gesperrt,
  geltung,
  id,
  feldname,
  symbol,
  stufen,
  wert,
  bezeichnung,
  breite,
  fremderHinweis,
  waehlen,
}: FeldProps) {
  const rahmenRef = useRef<HTMLSpanElement>(null);
  const gewaehltRef = useRef(false);
  // Der Hinweis beim Darüberfahren ist kontrolliert: Er schließt beim Aufklappen (läge sonst über der Überschrift
  // der Liste) und öffnet nur durch Maus oder Tastaturfokus auf dem Feld selbst. Die Liste liegt in einem Portal,
  // ihre Ereignisse steigen im React-Baum trotzdem bis hierher auf – etwa ein mouseover auf ihrem Hintergrund,
  // während sie nach der Wahl ausblendet; MUI öffnete den Hinweis davon erneut, obwohl die Maus längst im Vertrag
  // war, und ein mouseleave kam nie (Abschlussreview).
  const [hinweisOffen, setHinweisOffen] = useState(false);
  const geltungKlein = GELTUNG_TEXT[geltung].replace(/^G/, 'g');
  // Ein nicht wählbarer Wert (alte Zeilenhöhe 1.4 bzw. ein Wert, der nur per Schnittstelle entstehen kann) steht
  // als eigener Eintrag am Ende, damit das Feld ihn anzeigen kann.
  const eintraege = stufen.some((stufe) => stufe.wert === wert)
    ? stufen
    : [...stufen, { wert, bezeichnung, hinweis: fremderHinweis }];
  const feldFokussieren = () => rahmenRef.current?.querySelector<HTMLElement>('[role="combobox"]')?.focus();

  return (
    <Tooltip
      title={GELTUNG_TEXT[geltung]}
      open={hinweisOffen}
      onOpen={(event) => {
        if (rahmenRef.current?.contains(event.target as Node)) setHinweisOffen(true);
      }}
      onClose={() => setHinweisOffen(false)}
    >
      <span
        ref={rahmenRef}
        // Bevor MUI den Fokus ins Feld legt: die Schreibmarke ins Modell übernehmen (selectionchange kommt asynchron).
        onMouseDownCapture={() => {
          if (!gesperrt) editor.auswahlLeser.current?.();
        }}
      >
        <TextField
          select
          size="small"
          id={id}
          label={feldname}
          value={wert}
          disabled={gesperrt}
          data-testid={id}
          onChange={(event) => waehlen(Number(event.target.value))}
          SelectProps={{
            onOpen: () => {
              gewaehltRef.current = false;
              setHinweisOffen(false);
              editor.auswahlLeser.current?.();
            },
            // MUI ruft onChange nur bei einem anderen Wert; die Wahl selbst meldet der Klick auf den Eintrag.
            onClose: () => {
              if (gewaehltRef.current && editor.zustand.auswahl) editor.befehle.fokusAnfordern();
              else feldFokussieren();
            },
            renderValue: () => (
              <>
                <Box component="span" aria-hidden="true" sx={{ color: 'text.secondary', mr: 0.75 }}>
                  {symbol}
                </Box>
                {bezeichnung}
              </>
            ),
            MenuProps: {
              disableRestoreFocus: true,
              disableEnforceFocus: true,
              MenuListProps: {
                dense: true,
                subheader: (
                  <ListSubheader sx={{ lineHeight: 2.5 }}>{`${symbol} ${feldname} · ${geltungKlein}`}</ListSubheader>
                ),
              },
            },
          }}
          InputLabelProps={{ shrink: true }}
          // Kompakt wie die Knöpfe daneben, damit die feste Werkzeugleiste kaum höher wird.
          sx={{ minWidth: breite, '& .MuiSelect-select': { py: '4px', fontSize: '0.875rem' } }}
        >
          {eintraege.map((stufe) => (
            <MenuItem
              key={stufe.wert}
              value={stufe.wert}
              data-wert={stufe.wert}
              onClick={() => {
                gewaehltRef.current = true;
              }}
            >
              <ListItemIcon>{stufe.wert === wert && <CheckIcon fontSize="small" />}</ListItemIcon>
              <ListItemText primary={stufe.bezeichnung} secondary={stufe.hinweis} />
            </MenuItem>
          ))}
        </TextField>
      </span>
    </Tooltip>
  );
}

function Abstandswahl({ editor, gesperrt = false, geltung = 'vorlage' }: Props) {
  const { schrift } = editor.zustand.dokument;
  return (
    // Oben Platz für die Beschriftung, die auf dem Rahmen der Felder sitzt.
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pt: '7px' }} data-testid="vertrag-abstaende">
      <Abstandsfeld
        editor={editor}
        gesperrt={gesperrt}
        geltung={geltung}
        id="vertrag-zeilenabstand"
        feldname={ZEILENABSTAND_FELDNAME}
        symbol="↕"
        stufen={ZEILENABSTAENDE}
        wert={schrift.zeilenhoehe}
        bezeichnung={zeilenabstandBezeichnung(schrift.zeilenhoehe)}
        breite={128}
        fremderHinweis={schrift.zeilenhoehe === BISHERIGE_ZEILENHOEHE ? WIE_BISHER_HINWEIS : 'eigener Wert'}
        waehlen={(zeilenhoehe) => {
          if (!gesperrt) editor.befehle.zeilenabstand(zeilenhoehe);
        }}
      />
      <Abstandsfeld
        editor={editor}
        gesperrt={gesperrt}
        geltung={geltung}
        id="vertrag-absatzabstand"
        feldname={ABSATZABSTAND_FELDNAME}
        symbol="¶"
        stufen={ABSATZABSTAENDE}
        wert={absatzabstandEmVon(schrift)}
        bezeichnung={absatzabstandBezeichnung(schrift)}
        breite={156}
        fremderHinweis="eigener Wert"
        waehlen={(absatzabstandEm) => {
          if (!gesperrt) editor.befehle.absatzabstand(absatzabstandEm);
        }}
      />
    </Box>
  );
}

export default Abstandswahl;
